import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./LegalApp.css";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { Eye } from "lucide-react";

const ChargeSheetsList = ({ API_URL }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Take fir as input from previous page
    const fir = location.state?.fir ?? null;

    // --- Date formatting: ISO "2024-03-15T10:45:00Z" => "15/03/2024 10:45"
    const formatDateTime = (value) => {
        if (!value) return "-";

        // If value looks like ISO (has 'T'), parse normally
        if (typeof value === "string" && value.includes("T")) {
            const d = new Date(value);
            if (isNaN(d.getTime())) return "-";

            const pad = (n) => String(n).padStart(2, "0");
            const day = pad(d.getUTCDate());
            const month = pad(d.getUTCMonth() + 1);
            const year = d.getUTCFullYear();
            const hh = pad(d.getUTCHours());
            const mm = pad(d.getUTCMinutes());
            return `${day}/${month}/${year} ${hh}:${mm}`;
        }

        // If value is legacy like "24032025 15:00" (ddmmyyyy HH:mm)
        if (typeof value === "string" && value.length >= 8) {
            const dd = value.substring(0, 2);
            const mm = value.substring(2, 4);
            const yyyy = value.substring(4, 8);
            const timePart = value.substring(9, 14); // "HH:mm" if present
            if (dd && mm && yyyy) return `${dd}/${mm}/${yyyy}${timePart ? ` ${timePart}` : ""}`;
        }

        return String(value);
    };

    // Build rows for table (robust to missing fields)
    const rows = useMemo(() => {
        const out = [];

        const generatedReady = !!fir?.caseDiary?.chargeSheet;
        const verifiedReady = !!fir?.chargeSheet?.discrepanciesVerified || !!fir?.chargeSheet?.offensesAnalyzed;

        const cd = fir?.caseDiary?.uploadedOn;
        if (cd) {
            out.push({
                type: "Generate from Case Diary",
                date: fir?.caseDiary?.uploadedOn,
                viewPath: "/fir/chargesheet/generated",
                status: generatedReady ? "Charge Sheet Generated" : "Generating Charge Sheet...",
                isProcessing: !generatedReady,
            });
        }

        // Uploaded/Verified CS: per your requirement, stored at fir.chargeSheet.text
        const cs = fir?.chargeSheet?.uploadedOn;
        if (cs) {
            out.push({
                type: "Verify an existing Charge Sheet",
                date: fir?.chargeSheet?.uploadedOn,
                viewPath: "/fir/chargesheet/verified",
                status: verifiedReady ? "Verification Completed" : "Verifying Charge Sheet...",
                isProcessing: !verifiedReady,
            });
        }

        return out;
    }, [fir]);

    const showGenerateBtn = !fir?.caseDiary?.uploadedOn;     // if !fir.caseDiary || !fir.caseDiary.text
    const showVerifyBtn = !fir?.chargeSheet?.uploadedOn;    // if !fir.chargeSheet || !fir.chargeSheet.text

    if (!fir) {
        return (
            <div className="my-cases-container" style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
                <Navbar />
                <main style={{ flex: 1 }}>
                    <div className="info-section" style={{ marginTop: "120px", textAlign: "center" }}>
                        <b>Generated & Uploaded Charge Sheets</b>
                        <p style={{ marginTop: "12px" }}>No case diary or charge sheet was provided to this page.</p>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="my-cases-container" style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
            <Navbar />
            <main style={{ flex: 1 }}>
                <br />
                <br />

                {/* Page heading (similar pattern to MyCases) :contentReference[oaicite:2]{index=2} */}
                <div className="info-section" style={{ marginTop: "100px" }}>
                    <center>
                        <div style={{ position: "relative", textAlign: "center", marginBottom: "16px" }}>
                            <b style={{ display: "inline-block" }}>Generated & Uploaded Charge Sheets</b>
                        </div>
                    </center>
                </div>

                {/* Container that matches the table width and stays centered */}
                <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <div style={{ width: "min(1100px, 96%)" }}>
                        {/* Right-end buttons ABOVE the table (aligned to table, not page) */}
                        <div
                            style={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "flex-end",
                                marginBottom: "10px",
                            }}
                        >
                            <div style={{ display: "flex", gap: "10px" }}>
                                {showGenerateBtn && (
                                    <button
                                        onClick={() => navigate("/fir/casediary/upload", { state: { API_URL, fir } })}
                                        style={{
                                            padding: "6px 12px",
                                            backgroundColor: "#094268",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        Generate Charge Sheet
                                    </button>
                                )}

                                {showVerifyBtn && (
                                    <button
                                        onClick={() => navigate("/fir/chargesheet/upload", { state: { API_URL, fir } })}
                                        style={{
                                            padding: "6px 12px",
                                            backgroundColor: "#094268",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        Verify Charge Sheet
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Table (same container width) */}
                        <div style={{ overflowX: "auto" }}>
                            <table className="mycases-table" style={{ width: "100%" }}>
                                <thead>
                                    <tr>
                                        <th>Charge Sheet Type</th>
                                        <th>Generated/Uploaded Date</th>
                                        <th>Status</th>
                                        <th>View</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} style={{ textAlign: "center", padding: "16px" }}>
                                                No charge sheets found for this FIR.
                                            </td>
                                        </tr>
                                    ) : (
                                        rows.map((r, idx) => (
                                            <tr key={`${r.type}-${idx}`}>
                                                <td style={{ padding: "12px 8px", verticalAlign: "middle" }}>{r.type}</td>

                                                <td style={{ padding: "12px 8px", verticalAlign: "middle" }}>
                                                    {formatDateTime(r.date)}
                                                </td>

                                                <td style={{ padding: "12px 8px", verticalAlign: "middle" }}>
                                                    {r.status}
                                                </td>

                                                <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                                    <button
                                                        onClick={() => {
                                                            if (!r.isProcessing) {
                                                                navigate(r.viewPath, { state: { API_URL, fir } });
                                                            }
                                                        }}
                                                        disabled={r.isProcessing}
                                                        style={{
                                                            background: "none",
                                                            border: "none",
                                                            padding: 0,
                                                            cursor: r.isProcessing ? "not-allowed" : "pointer",
                                                            opacity: r.isProcessing ? 0.35 : 1,
                                                        }}
                                                        title={r.isProcessing ? "Processing..." : "View"}
                                                        aria-label="View"
                                                    >
                                                        <Eye size={16} style={{ stroke: "#094268" }} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {/* Back to Case Dashboard link */}
                        <div
                            style={{
                                marginTop: "14px",
                                display: "flex",
                                justifyContent: "flex-start",
                            }}
                        >
                            <span
                                onClick={() => navigate('/fir/dashboard', { state: { fir } })}
                                style={{
                                    cursor: "pointer",
                                    color: "#007AFF",
                                    fontWeight: 600,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    fontSize: "16px",
                                }}
                            >
                                <span aria-hidden="true" style={{ fontSize: "18px", lineHeight: "14px" }}>
                                    ←
                                </span>
                                Case Dashboard
                            </span>
                        </div>
                    </div>
                </div>

            </main>

            <Footer />
        </div>
    );
};

export default ChargeSheetsList;